import React, { createContext, useContext, useState, useEffect } from "react";

interface ColorContextType {
  themeColor: string;
  setThemeColor: (color: string) => void;
  logoName: string;
  setLogoName: (name: string) => void;
}

const ColorContext = createContext<ColorContextType | undefined>(undefined);

export const useColor = () => {
  const context = useContext(ColorContext);
  if (context === undefined) {
    throw new Error("useColor must be used within a ColorProvider");
  }
  return context;
};

interface ColorProviderProps {
  children: React.ReactNode;
}

export const ColorProvider: React.FC<ColorProviderProps> = ({ children }) => {
  // Initialize with saved color or default
  const [themeColor, setThemeColor] = useState(() => {
    const savedColor = localStorage.getItem("themeColor");
    return savedColor || "#2563EB";
  });

  // Initialize with saved logo name or default
  const [logoName, setLogoName] = useState(() => {
    const savedLogoName = localStorage.getItem("logoName");
    return savedLogoName || "Kanton Luzern";
  });

  // Apply color to CSS custom properties whenever it changes
  useEffect(() => {
    localStorage.setItem("themeColor", themeColor);

    // Update CSS custom properties
    const root = document.documentElement;
    root.style.setProperty("--theme-color", themeColor);

    // Also update the primary, accent, and destructive colors
    root.style.setProperty("--primary", themeColor);
    root.style.setProperty("--accent", themeColor);
    root.style.setProperty("--destructive", themeColor);
  }, [themeColor]);

  // Save logo name to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("logoName", logoName);
  }, [logoName]);

  const value = {
    themeColor,
    setThemeColor,
    logoName,
    setLogoName,
  };

  return (
    <ColorContext.Provider value={value}>{children}</ColorContext.Provider>
  );
};
